/* eslint-disable */
/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

const FundRequestReview: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const { userProfile } = useAuth();
  const companyId = userProfile?.company_id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [request, setRequest] = useState<any>(null);
  const [project, setProject] = useState<any>(null);
  const [companyProfile, setCompanyProfile] = useState<any>(null);
  const [companyEntities, setCompanyEntities] = useState<any[]>([]);
  const [selectedEntityId, setSelectedEntityId] = useState<string>('foundation_direct');
  const [action, setAction] = useState<'approve' | 'reject' | 'clarify' | null>(null);
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (requestId) {
      loadRequest();
    }
  }, [requestId]);

  const loadRequest = async () => {
    if (!requestId) return;

    const { data, error } = await supabase
      .from('fund_requests')
      .select('*')
      .eq('id', requestId)
      .single();

    if (!error && data) {
      setRequest(data);

      // Load project details
      const { data: projectData } = await supabase
        .from('projects')
        .select('*')
        .eq('id', data.project_id)
        .single();

      if (projectData) {
        setProject(projectData);
      }

      // Load company profile
      if (companyId) {
        const { data: companyData } = await supabase
          .from('companies')
          .select('name')
          .eq('id', companyId)
          .single();

        if (companyData) {
          setCompanyProfile(companyData);
        }

        // Load company entities
        const { data: entitiesData } = await supabase
          .from('company_entities')
          .select('*')
          .eq('company_id', companyId)
          .order('name');

        if (entitiesData) {
          setCompanyEntities(entitiesData);
        }
      }
    }
  };

  const handleViewDoc = async (e: React.MouseEvent, url: string, download: boolean = false) => {
    e.preventDefault();
    if (!url) return;

    if (!url.includes('/storage/v1/object/public/')) {
      window.open(url, '_blank');
      return;
    }

    try {
      const parts = url.split('/storage/v1/object/public/');
      const afterPublic = parts[1];
      const pathParts = afterPublic.split('/');

      if (pathParts.length > 1) {
        const bucketName = pathParts[0];
        const pathInBucket = pathParts.slice(1).join('/');

        const { data } = await supabase.storage.from(bucketName).createSignedUrl(decodeURIComponent(pathInBucket), 3600);

        if (data && data.signedUrl) {
          if (download) {
            const a = document.createElement('a');
            a.href = data.signedUrl;
            a.download = '';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          } else {
            window.open(data.signedUrl, '_blank');
          }
          return;
        }
      }
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error opening document:', err);
      window.open(url, '_blank');
    }
  };

  const handleAction = async (actionType: 'approve' | 'reject' | 'clarify') => {
    if (actionType === 'reject' && !comment.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    if (actionType === 'clarify' && !comment.trim()) {
      setError('Please provide clarification request');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !requestId) throw new Error('Not authenticated');

      const updateData: any = {
        status: actionType === 'approve' ? 'approved' : actionType === 'reject' ? 'rejected' : 'clarification_requested',
        updated_at: new Date().toISOString()
      };

      if (actionType === 'approve') {
        updateData.approved_by = user.id;
        updateData.approved_at = new Date().toISOString();
      } else if (actionType === 'reject') {
        updateData.rejected_reason = comment;
      } else if (actionType === 'clarify') {
        updateData.clarification_request = comment;
      }

      const { error: updateError } = await supabase
        .from('fund_requests')
        .update(updateData)
        .eq('id', requestId);

      if (updateError) throw updateError;

      // If approved, create fund disbursement record
      if (actionType === 'approve' && request) {
        const isFoundationDirect = selectedEntityId === 'foundation_direct';
        const isFoundationVia = selectedEntityId === 'foundation_via';

        let entityId = null;
        let routingMethod = 'via_foundation';

        if (isFoundationVia) {
          routingMethod = 'via_foundation';
        } else if (!isFoundationDirect && !isFoundationVia) {
          entityId = selectedEntityId;
          routingMethod = 'direct_to_project';
        }

        const { data: disbursementData, error: disbursementError } = await supabase
          .from('fund_disbursements')
          .insert({
            project_id: request.project_id,
            ngo_id: request.ngo_id,
            amount: request.amount,
            fund_request_id: requestId,
            released_by: user.id,
            released_at: new Date().toISOString(),
            tranche_number: request.tranche_number || null,
            notes: `Fund release approved for tranche ${request.tranche_number || 'N/A'}`,
            entity_id: entityId,
            routing_method: routingMethod
          })
          .select();

        if (disbursementError) {
          console.error('Error creating fund disbursement:', disbursementError);
          console.error('Error details:', {
            message: disbursementError.message,
            details: disbursementError.details,
            hint: disbursementError.hint,
            code: disbursementError.code
          });
          // Show error but don't fail the whole operation
          setError(`Fund request approved, but failed to create disbursement record: ${disbursementError.message}. Please check RLS policies.`);
          // Still navigate, but show the error
          setTimeout(() => {
            navigate(`/client/company/finance`);
          }, 2000);
          return;
        } else {
        }
      }

      navigate(`/client/company/finance`);
    } catch (err: any) {
      setError(err.message || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  if (!request || !project) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <div className="form-section">
        <h1>Review Fund Request</h1>
        <p style={{ color: 'var(--muted)', marginBottom: '2rem' }}>
          Review and process fund disbursement request
        </p>

        {error && (
          <div className="toast toast-error" style={{ position: 'relative', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <div className="card">
          <h2>Request Details</h2>
          <div className="form-row">
            <div>
              <strong>Project:</strong>
              <p>{project.name}</p>
            </div>
            <div>
              <strong>Amount:</strong>
              <p>₹{parseFloat(request.amount).toLocaleString('en-IN')}</p>
            </div>
            <div>
              <strong>Status:</strong>
              <p>
                <span className={`status-badge status-${request.status}`}>
                  {request.status}
                </span>
              </p>
            </div>
          </div>

          {request.tranche_number && (
            <div>
              <strong>Tranche Number:</strong>
              <p>{request.tranche_number}</p>
            </div>
          )}

          <div style={{ marginTop: '1rem' }}>
            <strong>Purpose:</strong>
            <p style={{ whiteSpace: 'pre-wrap' }}>{request.purpose}</p>
          </div>

          {request.attachments && Array.isArray(request.attachments) && request.attachments.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <strong>Attachments:</strong>
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                {request.attachments.map((url: string, index: number) => (
                  <li key={index}>
                    <button
                      onClick={(e) => handleViewDoc(e, url)}
                      className="btn btn-sm btn-outline"
                      style={{ border: 'none', background: 'transparent', padding: 0, textDecoration: 'underline', color: 'var(--primary)', cursor: 'pointer' }}
                    >
                      View Attachment {index + 1}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {(request.status === 'pending' || request.status === 'clarification_requested') && (
          <div className="card">
            <h2>Action</h2>

            <div className="form-group">
              <label>Comment / Reason</label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={
                  action === 'reject'
                    ? 'Provide reason for rejection...'
                    : action === 'clarify'
                      ? 'Request clarification...'
                      : 'Optional comment...'
                }
                rows={4}
              />
            </div>

            <div className="form-group" style={{ marginTop: '1rem', padding: '1rem', background: 'var(--background)', borderRadius: '8px', border: '1px solid var(--border)' }}>
              <label>Funding Source {companyProfile?.setup_type === 'single' ? '' : '(Company/Unit)'} (Required for Approval)</label>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                Select whether this fund release comes directly from a specific unit or from the Foundation account.
              </p>
              <select
                className="form-control"
                value={selectedEntityId}
                onChange={(e) => setSelectedEntityId(e.target.value)}
                style={{ width: '100%', padding: '0.5rem' }}
              >
                <option value="foundation_direct">Foundation Account</option>
                {companyEntities.map(entity => (
                  <option key={entity.id} value={entity.id}>
                    {entity.name} {entity.is_escrow ? '(Escrow Account)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-actions" style={{ marginTop: '2rem' }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => navigate(`/client/company/budget`)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-warning"
                onClick={() => {
                  setAction('clarify');
                  if (comment.trim()) handleAction('clarify');
                }}
                disabled={loading}
              >
                Request Clarification
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => {
                  setAction('reject');
                  if (comment.trim()) handleAction('reject');
                }}
                disabled={loading}
              >
                Reject
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => handleAction('approve')}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Approve'}
              </button>
            </div>
          </div>
        )}

        {request.status === 'clarification_requested' && request.clarification_request && (
          <div className="card" style={{ background: '#fef3c7' }}>
            <h3>Clarification Requested</h3>
            <p>{request.clarification_request}</p>
          </div>
        )}

        {request.status === 'rejected' && request.rejected_reason && (
          <div className="card" style={{ background: '#fee2e2' }}>
            <h3>Rejection Reason</h3>
            <p>{request.rejected_reason}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FundRequestReview;


